import React, { useEffect, useRef, useState } from "react";
import { Music2, User } from "lucide-react";
import {
  ONBOARDING_STEP1_FIELD_ORDER,
  ONBOARDING_STEP2_FIELD_ORDER,
  PROFILE_FIELD_IDS,
  scrollToFirstProfileFieldError,
  validateOnboardingStep1,
  validateOnboardingStep2,
  validatePersonalForm,
} from "../../lib/profileForm";
import { ReadableCard, ReadableNavButton } from "../Sticker";
import FormTextField from "../FormTextField";
import DanceRoleField from "../DanceRoleField";
import DanceFormsField from "../DanceFormsField";
import {
  FieldHint,
  FORM_STYLE_CARD_CLASS,
  FormValidationAlert,
  SectionTitle,
} from "../createEvent/CreateEventFormUi";

const ONBOARDING_FORM_ID = "profile-onboarding-form";

function ProfileOnboardingProgress({ step }) {
  return (
    <div
      className="mt-4 flex items-center gap-2"
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={2}
      aria-valuenow={step}
      aria-label={`Step ${step} of 2`}
      data-testid="profile-onboarding-progress"
    >
      <span className="font-ui text-[12px] font-medium text-ink/70 shrink-0">Step {step} of 2</span>
      <div className="flex flex-1 items-center gap-1.5" aria-hidden>
        <span
          className={`h-1.5 flex-1 rounded-full transition-colors ${
            step >= 1 ? "bg-plum" : "bg-black/[0.08]"
          }`}
        />
        <span
          className={`h-1.5 flex-1 rounded-full transition-colors ${
            step >= 2 ? "bg-plum" : "bg-black/[0.08]"
          }`}
        />
      </div>
    </div>
  );
}

function OnboardingStickyBar({ step, saving, dirty, onContinue, onBack }) {
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 sm:hidden border-t border-black/[0.08] bg-canvas px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]"
      data-testid="profile-onboarding-sticky-bar"
      aria-busy={saving}
    >
      <div className="max-w-page mx-auto">
        {dirty && step === 2 ? (
          <p
            className="font-ui text-[12px] font-medium text-ink/75 text-center mb-1.5"
            data-testid="profile-unsaved-hint"
          >
            Unsaved changes
          </p>
        ) : null}
        <div className="flex items-stretch gap-2">
          {step === 2 ? (
            <ReadableNavButton
              type="button"
              onClick={onBack}
              disabled={saving}
              className="shrink-0 px-3 text-[13px]"
              data-testid="onboarding-back-btn"
            >
              ← Back
            </ReadableNavButton>
          ) : null}
          {step === 1 ? (
            <ReadableNavButton
              type="button"
              variant="primary"
              onClick={onContinue}
              disabled={saving}
              className="w-full text-[14px]"
              data-testid="onboarding-continue-btn"
            >
              Continue →
            </ReadableNavButton>
          ) : (
            <ReadableNavButton
              type="submit"
              form={ONBOARDING_FORM_ID}
              variant="primary"
              disabled={saving}
              className="flex-1 min-w-0 text-[13px]"
              data-testid="sticky-save-profile-btn"
            >
              <span className="truncate">{saving ? "Saving…" : "Save & continue →"}</span>
            </ReadableNavButton>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProfileOnboardingWizard({
  name,
  onNameChange,
  danceForm,
  onRoleChange,
  onDanceFormsChange,
  fieldErrors,
  onSetFieldErrors,
  onClearFieldError,
  loadingDance,
  saving,
  dirty,
  account,
  organizer,
  onSave,
}) {
  organizer = account ?? organizer;
  const [step, setStep] = useState(1);
  const roleSectionRef = useRef(null);

  useEffect(() => {
    if (step !== 2) return;
    const reduceMotion =
      typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    requestAnimationFrame(() => {
      const focusable = roleSectionRef.current?.querySelector?.("button");
      focusable?.focus({ preventScroll: true });
      if (!reduceMotion && roleSectionRef.current) {
        roleSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }, [step]);

  function scrollToErrors(errors, fieldOrder) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollToFirstProfileFieldError(errors, fieldOrder));
    });
  }

  function handleContinue() {
    const errors = validateOnboardingStep1({ form: { name } });
    if (Object.keys(errors).length > 0) {
      onSetFieldErrors(errors);
      scrollToErrors(errors, ONBOARDING_STEP1_FIELD_ORDER);
      return;
    }
    onSetFieldErrors({});
    setStep(2);
  }

  function handleBack() {
    onSetFieldErrors({});
    setStep(1);
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errors = validatePersonalForm({
      form: { name },
      danceForm,
      organizer,
      onboarding: true,
    });
    if (Object.keys(errors).length > 0) {
      onSetFieldErrors(errors);
      if (errors.name) {
        setStep(1);
        scrollToErrors(errors, ONBOARDING_STEP1_FIELD_ORDER);
      } else {
        const step2Only = validateOnboardingStep2({ danceForm });
        scrollToErrors(step2Only, ONBOARDING_STEP2_FIELD_ORDER);
      }
      return;
    }
    onSetFieldErrors({});
    onSave(e);
  }

  return (
    <>
      <ProfileOnboardingProgress step={step} />

      <form
        id={ONBOARDING_FORM_ID}
        onSubmit={handleSubmit}
        className="mt-6 space-y-6"
        aria-busy={saving || loadingDance}
        noValidate
      >
        <FormValidationAlert count={Object.keys(fieldErrors).length} />

        {step === 1 ? (
          <ReadableCard className={FORM_STYLE_CARD_CLASS} data-testid="onboarding-step-1">
            <SectionTitle icon={User}>About you</SectionTitle>
            <FieldHint className="text-[12px] sm:text-[13px]">
              Home city is optional — add it later on your profile.
            </FieldHint>
            <FormTextField
              id={PROFILE_FIELD_IDS.name}
              label="Name"
              required
              error={fieldErrors.name}
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Marta"
              autoComplete="name"
              maxLength={100}
              data-testid="name-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleContinue();
                }
              }}
            />

            <div className="hidden sm:flex justify-end pt-2 border-t border-black/[0.08] mt-4">
              <ReadableNavButton
                type="button"
                variant="primary"
                onClick={handleContinue}
                disabled={saving}
                data-testid="onboarding-continue-desktop-btn"
              >
                Continue →
              </ReadableNavButton>
            </div>
          </ReadableCard>
        ) : (
          <ReadableCard className={FORM_STYLE_CARD_CLASS} data-testid="onboarding-step-2">
            <SectionTitle icon={Music2}>How do you dance?</SectionTitle>
            <FieldHint className="text-[12px] sm:text-[13px]">
              Hosts see this on your RSVP.
            </FieldHint>

            {loadingDance ? (
              <div className="animate-pulse space-y-3" aria-busy="true" aria-label="Loading dance profile">
                <div className="h-10 w-56 max-w-full rounded-md2 bg-black/[0.06]" />
                <div className="h-10 w-full rounded-md2 bg-black/[0.06]" />
                <p className="sr-only">Loading dance profile…</p>
              </div>
            ) : (
              <>
                <div ref={roleSectionRef}>
                  <DanceRoleField
                    id={PROFILE_FIELD_IDS.role}
                    required
                    value={danceForm.role}
                    onChange={(v) => {
                      onClearFieldError("role");
                      onRoleChange(v);
                    }}
                    testIdPrefix="profile-role"
                    error={fieldErrors.role}
                    errorId={`${PROFILE_FIELD_IDS.role}-error`}
                  />
                </div>

                <DanceFormsField
                  id={PROFILE_FIELD_IDS.dance_forms}
                  value={danceForm.dance_forms}
                  onChange={(dance_forms) => {
                    onClearFieldError("dance_forms");
                    onDanceFormsChange(dance_forms);
                  }}
                  error={fieldErrors.dance_forms}
                  errorId={`${PROFILE_FIELD_IDS.dance_forms}-error`}
                />
              </>
            )}

            <div className="hidden sm:flex flex-wrap justify-end gap-2 pt-2 border-t border-black/[0.08]">
              <ReadableNavButton
                type="button"
                onClick={handleBack}
                disabled={saving}
                data-testid="onboarding-back-desktop-btn"
              >
                ← Back
              </ReadableNavButton>
              <ReadableNavButton
                type="submit"
                variant="primary"
                disabled={saving || loadingDance}
                data-testid="save-profile-btn"
              >
                {saving ? "Saving…" : "Save & continue →"}
              </ReadableNavButton>
            </div>
          </ReadableCard>
        )}
      </form>

      <OnboardingStickyBar
        step={step}
        saving={saving}
        dirty={dirty}
        onContinue={handleContinue}
        onBack={handleBack}
      />
    </>
  );
}
